import React, { useEffect, Fragment, useState } from "react";
import Breadcrumb from "../../../components/common/breadcrumb";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import DataTable from "react-data-table-component";
import { CSVLink } from "react-csv";
import { showToast } from "../../../components/ToastifyNotification";
import { UncontrolledTooltip } from "reactstrap";
import Swal from "sweetalert2";
import { DeleteMessage, OutboxMessages } from "../../../api/messaging/messageAPI";

const truncate = (text, length = 60) =>
    text?.length > length ? text.substring(0, length) + "..." : text;

const Outbox = () => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [search, setSearch] = useState("");
  const navigate = useNavigate();
  const dispatch = useDispatch();

  /* ---------------------------------
     FETCH OUTBOX
  ----------------------------------*/
  const fetchData = async () => {
    dispatch({ type: "LOADER_START" });
    try {
      const response = await OutboxMessages();

      if (response.success) {
        showToast("success", response.message);
        const formattedData = response.data.map((item, index) => ({
          index: index + 1,
          id: item._id,

          toUsers: item.toUserIds
            ?.map(u => u.userId?.userName)
            .join(", "),

          ccUsers: item.ccUserIds
            ?.map(u => u.userId?.userName)
            .join(", "),

          subject: item.subject || "",
          message: item.message || "",
          messagePreview: truncate(item.message),
          date: new Date(item.createdAt).toLocaleString()
        }));

        setData(formattedData);
        setFilteredData(formattedData);
      } else {
        showToast("error", response.message);
      }
    } catch (error) {
      showToast("error", error.message || "Something went wrong");
    } finally {
      dispatch({ type: "LOADER_STOP" });
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  /* ---------------------------------
     SEARCH
  ----------------------------------*/
  useEffect(() => {
    const results = data.filter(item =>
      Object.values(item)
        .join(" ")
        .toLowerCase()
        .includes(search.toLowerCase())
    );
    setFilteredData(results);
  }, [search, data]);

  /* ---------------------------------
     ACTIONS
  ----------------------------------*/
  const handleViewClick = id => {
    navigate(`/messaging/view/${id}`);
  };

  const handleDeleteClick = async id => {
    Swal.fire({
      title: "Are you sure?",
      text: "This message will be removed from your outbox",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!"
    }).then(async result => {
      if (result.isConfirmed) {
        dispatch({ type: "LOADER_START" });
        try {
          const response = await DeleteMessage(id);
          if (response.success) {
            showToast("success", response.message);
            setData(prev => prev.filter(item => item.id !== id));
            setFilteredData(prev => prev.filter(item => item.id !== id));
          } else {
            showToast("error", response.message);
          }
        } catch (error) {
          showToast("error", error.message || "Delete failed");
        } finally {
          dispatch({ type: "LOADER_STOP" });
        }
      }
    });
  };

  /* ---------------------------------
     TABLE COLUMNS
  ----------------------------------*/
  const columns = [
    { name: "S.No", selector: row => row.index, width: "8%" },
    { name: "To", selector: row => row.toUsers, sortable: true, width:"15%" },
    { name: "CC", selector: row => row.ccUsers, sortable: true, width:"15%" },
    { name: "Subject", selector: row => row.subject, sortable: true, width:"22%" },
     {
      name: "Message",
      selector: row => row.messagePreview, width:"15%"
    },
    { name: "Date", selector: row => row.date, sortable: true, width:"10%" },
    {
      name: "Actions",
      cell: row => (
        <div>
          <button
            className="btn btn-primary me-2"
            onClick={() => handleViewClick(row.id)}
            id={`view-${row.id}`}
          >
            <i className="fa fa-eye" />
          </button>
          <UncontrolledTooltip placement="top" target={`view-${row.id}`}>
            View
          </UncontrolledTooltip>

          <button
            className="btn btn-danger"
            onClick={() => handleDeleteClick(row.id)}
            id={`delete-${row.id}`}
          >
            <i className="fa fa-trash-o" />
          </button>
          <UncontrolledTooltip placement="top" target={`delete-${row.id}`}>
            Delete
          </UncontrolledTooltip>
        </div>
      ),
      ignoreRowClick: true,
      button: true, width:"15%"
    }
  ];

  /* ---------------------------------
     CSV EXPORT
  ----------------------------------*/
  const headers = [
    { label: "S.No", key: "index" },
    { label: "To", key: "toUsers" },
    { label: "CC", key: "ccUsers" },
    { label: "Subject", key: "subject" },
    { label: "Message", key: "message" },
    { label: "Date", key: "date" }
  ];

  const ExportCSV = ({ data }) => (
    <CSVLink data={data} headers={headers} filename="outbox.csv">
      <button className="btn btn-primary" id="export">
        Export
      </button>
    </CSVLink>
  );

  /* ---------------------------------
     UI
  ----------------------------------*/
  return (
    <Fragment>
      <Breadcrumb parent="Messaging" title="Outbox" />
      <div className="container-fluid">
        <div className="card">
          <div className="card-header d-flex justify-content-between">
            <h5>Outbox</h5>
          </div>

          <div className="card-body">
            <div className="row mb-2">
              <div className="col-sm-6">
                <input
                  className="form-control"
                  type="text"
                  placeholder="Search messages..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
              </div>
              <div className="col-sm-6 text-end">
                <ExportCSV data={data} />
              </div>
            </div>

            <DataTable
              columns={columns}
              data={filteredData}
              pagination
              highlightOnHover
            />
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default Outbox;
